import { Fragment } from "react";
import { MoreVertical, type LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

export interface RowAction {
  label: string;
  icon?: LucideIcon;
  onSelect: () => void;
  disabled?: boolean;
  // Renders in destructive red and is separated from the rest — the first
  // destructive item gets the divider, so callers list actions in display order
  // without positioning it themselves.
  destructive?: boolean;
}

export interface RowActionsMenuProps {
  actions: readonly RowAction[];
  // Heading inside the menu.
  label?: string;
  // Accessible name for the trigger, which is icon-only.
  triggerLabel?: string;
  className?: string;
}

// The per-row actions menu at the right end of a list table.
//
// Styled to match the date-range picker's preset card — 208px wide, a muted
// heading, 40px rows — so the two menus in a list page read as one family.
//
// The column that hosts this carries no header text: the trigger is the whole
// affordance, and `triggerLabel` is what assistive tech announces instead.
export function RowActionsMenu({
  actions,
  label = "Actions",
  triggerLabel = "Row actions",
  className,
}: RowActionsMenuProps): React.ReactElement | null {
  if (actions.length === 0) return null;

  const firstDestructive: number = actions.findIndex(
    (action) => action.destructive
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={triggerLabel}
          className={cn(
            "text-muted-foreground size-8",
            "hover:bg-muted hover:text-foreground",
            // A darker rest state while open, so the open menu shows which row it
            // belongs to.
            "data-[state=open]:bg-muted data-[state=open]:text-foreground",
            className
          )}
          // The row itself is often clickable; the menu is its own target.
          onClick={(event) => event.stopPropagation()}
        >
          <MoreVertical className="size-4" />
        </Button>
      </DropdownMenuTrigger>

      {/* Padding on the label and rows rather than the panel, matching the
          date-range preset card. */}
      <DropdownMenuContent align="end" className="w-52 overflow-hidden p-0">
        <div className="px-2 pt-3 pb-2">
          <DropdownMenuLabel className="text-muted-foreground mb-2 px-3 py-0 text-xs font-medium">
            {label}
          </DropdownMenuLabel>

          {actions.map((action, index) => {
            const Icon = action.icon;
            return (
              <Fragment key={`${action.label}-${index}`}>
                {index === firstDestructive && index > 0 && (
                  <DropdownMenuSeparator className="my-2" />
                )}
                <DropdownMenuItem
                  disabled={action.disabled}
                  onSelect={action.onSelect}
                  className={cn(
                    "min-h-10 rounded-md px-3 text-sm",
                    action.destructive &&
                      "text-destructive focus:text-destructive"
                  )}
                >
                  {Icon && <Icon className="size-4" />}
                  {action.label}
                </DropdownMenuItem>
              </Fragment>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
